import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { Box, Button, Collapse, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { ReactComponent as MonitorIcon } from "assets/images/icons/btn_monitor_light.svg";
import { ReactComponent as PlaybackIcon } from "assets/images/icons/btn_report.svg";
import ViewItem from "./ViewItem";
import CameraSearchPanel from "./CameraSearchPanel";

/**
 * ResourceSidebar
 * Contains the Live View / Playback toggle and the list of saved views.
 * Will also hold Device (Camera/NVR) lists and Favorites.
 *
 * Props:
 *   views       - array of view objects
 *   onEditView  - called with view when settings icon is clicked (opens EditViewModal)
 *   onPlayView  - called with view when play icon is clicked
 *   onPlayCamera - called with camera when device row is clicked
 *   onToggleFavorite - called with deviceId when star is toggled
 */
const ResourceSidebar = ({
  views,
  devices = [],
  nvrs = [],
  onEditView,
  onPlayView,
  onPlayCamera,
  onPlayCameras,
  onToggleFavorite,
  onDeviceSettings,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [viewsExpanded, setViewsExpanded] = useState(true);

  const panelSx = {
    backgroundColor: theme.palette.primary.white,
    borderRight: "1px solid rgba(0,0,0,0.1)",
    borderRadius: `${theme.shape.customBorderRadius[4]} 0 0 ${theme.shape.customBorderRadius[4]}`,
    width: 300,
    minWidth: 270,
    display: "flex",
    flexDirection: "column",
    boxShadow: "1px 1px 4px 0 rgba(0,0,0,0.2)",
  };

  const toggleBtnBase = {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    py: 1,
    fontSize: 14,
    fontWeight: 600,
    borderRadius: 0,
    textTransform: "none",
    minWidth: 0,
  };

  return (
    <Box sx={panelSx}>
      {/* Live View / Playback toggle */}
      <Box
        sx={{
          display: "flex",
          m: "16px 12px",
          border: `1.5px solid ${theme.palette.primary.main}`,
          borderRadius: theme.shape.customBorderRadius[1],
          overflow: "hidden",
        }}
      >
        <Button
          disableRipple
          sx={{
            ...toggleBtnBase,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.white,
            "&:hover": { backgroundColor: theme.palette.primary.main },
          }}
        >
          <MonitorIcon width={28} height={28} />
          <FormattedMessage id="liveview" />
        </Button>
        <Button
          disableRipple
          onClick={() => navigate("/surveillance/playback")}
          sx={{
            ...toggleBtnBase,
            backgroundColor: "transparent",
            color: theme.palette.primary.main,
            "&:hover": { backgroundColor: "rgba(23,38,100,0.06)" },
          }}
        >
          <PlaybackIcon width={28} height={28} />
          <FormattedMessage id="playback" />
        </Button>
      </Box>

      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflowY: "auto", pb: "12px" }}>
        {/* View collapsible section */}
        <Box sx={{ mt: "16px", mx: "12px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              px: "8px",
              py: "6px",
              backgroundColor: theme.palette.primary.background,
              cursor: "pointer",
            }}
            onClick={() => setViewsExpanded((v) => !v)}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              {viewsExpanded ? (
                <ArrowDropDownIcon sx={{ fontSize: 20, color: theme.palette.primary.dark }} />
              ) : (
                <ArrowRightIcon sx={{ fontSize: 20, color: theme.palette.primary.dark }} />
              )}
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: theme.palette.primary.dark }}>
                View ({views.length})
              </Typography>
            </Box>
          </Box>
          <Collapse in={viewsExpanded}>
            <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
              {views.map((view) => (
                <ViewItem
                  key={view.id}
                  view={view}
                  onEdit={onEditView}
                  onPlay={onPlayView}
                />
              ))}
            </Box>
          </Collapse>
        </Box>

        {/* Camera search panel — Search / Favorite / Device / NVR */}
        <CameraSearchPanel
          devices={devices}
          nvrs={nvrs}
          onDeviceSettings={onDeviceSettings}
          onPlayCamera={onPlayCamera}
          onPlayCameras={onPlayCameras}
          onToggleFavorite={onToggleFavorite}
        />
      </Box>
    </Box>
  );
};

export default ResourceSidebar;
